import {
  ConnectorStatusType,
  ConnectorRecord,
  ConnectorStatus,
  ConnectorEnum,
  getEventhouseProvisionState,
} from '@/types/connector'

// Mirrors backend/web/src/connectors/connector-health.ts. Only reached when the
// API predates the server-derived `health` field.
const GRACE_BEATS = 2
const MIN_GRACE_SECONDS = 60
const DEFAULT_INTERVAL_SECONDS = 60

const graceSeconds = (intervalConnection: number): number => {
  const interval =
    Number.isFinite(intervalConnection) && intervalConnection > 0
      ? intervalConnection
      : DEFAULT_INTERVAL_SECONDS
  return Math.max(MIN_GRACE_SECONDS, interval * GRACE_BEATS)
}

/**
 * Whether a connector is still beating.
 *
 * The verdict is the SERVER's (`connector.health`) so the page, an alert, and a
 * test all agree on one rule. The local fallback below is only for a response
 * that predates that field, and it applies the same rule: a connector is late
 * relative to ITS OWN `interval_connection`, not to a flat 60s window. That
 * window used to be hardcoded here, which read a healthy 5-minute connector as
 * disconnected for 80% of every beat cycle (98% on an hourly one).
 */
export const getConnectorStatus = (
  connector: ConnectorRecord
): ConnectorStatusType => {
  if (connector.health) {
    return connector.health.status === 'online'
      ? ConnectorStatus.ONLINE
      : ConnectorStatus.DISCONNECTED
  }

  if (!connector.active) {
    return ConnectorStatus.DISCONNECTED
  }
  // An eventhouse never heartbeats — `last_connection` is stamped once at link
  // time — so aging it would report every linked one as down. Only 'linked'
  // means live; `active` alone is not proof, since the activate endpoint sets
  // it on any type without checking.
  if (connector.type === ConnectorEnum.EVENTHOUSE) {
    return getEventhouseProvisionState(connector).status === 'linked'
      ? ConnectorStatus.ONLINE
      : ConnectorStatus.DISCONNECTED
  }
  if (!connector.last_connection) {
    return ConnectorStatus.DISCONNECTED
  }
  const elapsedSeconds =
    (new Date().getTime() - new Date(connector.last_connection).getTime()) / 1000
  return elapsedSeconds <= graceSeconds(connector.interval_connection)
    ? ConnectorStatus.ONLINE
    : ConnectorStatus.DISCONNECTED
}
